import {
  Button,
  EmptyState,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  cn,
} from '@startupquickstart/frontend';
import { ChevronLeft, ChevronRight, ImageOff } from 'lucide-react';
import { useMemo, useState } from 'react';

import { SubmissionCard } from '@/components/submissions/SubmissionCard';
import { SubmissionReviewDrawer } from '@/components/submissions/SubmissionReviewDrawer';
import {
  DEFAULT_PAGE_SIZE,
  STATUS_FILTER,
  useEventChallengeOptions,
  useEventSubmissions,
  useEventTeamOptions,
  useSubmissionsSummary,
  type StatusFilter,
  type Submission,
  type SubmissionsSummary,
} from '@/components/submissions/useSubmissions';

/** Sentinel value for the "any" option in a Select (Radix disallows ''). */
const ANY_OPTION = 'all';

/** Ordered status pills with the summary key that feeds each live count. */
const STATUS_PILLS: ReadonlyArray<{
  value: StatusFilter;
  label: string;
  countKey: keyof SubmissionsSummary;
}> = [
  { value: STATUS_FILTER.pending, label: 'Pending', countKey: 'pending' },
  { value: STATUS_FILTER.approved, label: 'Approved', countKey: 'approved' },
  { value: STATUS_FILTER.rejected, label: 'Rejected', countKey: 'rejected' },
  { value: STATUS_FILTER.all, label: 'All', countKey: 'total' },
];

interface SubmissionsGalleryProps {
  eventId: string;
  /** When set, scopes to one challenge and hides the challenge filter. */
  fixedChallengeId?: string;
}

/**
 * The main submissions review surface: status pills with live counts,
 * challenge/team filters, a responsive card grid, pagination, and a
 * lightbox-style review drawer. Reused at the event level and, scoped via
 * `fixedChallengeId`, on a challenge's Submissions tab.
 */
export function SubmissionsGallery({ eventId, fixedChallengeId }: SubmissionsGalleryProps) {
  const [status, setStatus] = useState<StatusFilter>(STATUS_FILTER.pending);
  const [challengeId, setChallengeId] = useState<string>(ANY_OPTION);
  const [teamId, setTeamId] = useState<string>(ANY_OPTION);
  const [page, setPage] = useState(1);
  const [drawerIndex, setDrawerIndex] = useState<number | null>(null);

  const effectiveChallengeId = fixedChallengeId ?? (challengeId === ANY_OPTION ? undefined : challengeId);

  const { data: summaryData } = useSubmissionsSummary(eventId);
  const summary = summaryData?.data;

  const { data: teamsData } = useEventTeamOptions(eventId);
  const { data: challengesData } = useEventChallengeOptions(eventId, !fixedChallengeId);

  const { data, isLoading, isError } = useEventSubmissions(eventId, {
    photoStatus: status === STATUS_FILTER.all ? undefined : status,
    challengeId: effectiveChallengeId,
    teamId: teamId === ANY_OPTION ? undefined : teamId,
    page,
    limit: DEFAULT_PAGE_SIZE,
  });

  const submissions = useMemo(() => data?.data ?? [], [data]);
  const pagination = data?.pagination;

  /** Change a filter and reset pagination to the first page. */
  const applyStatus = (next: StatusFilter) => {
    setStatus(next);
    setPage(1);
  };
  const applyChallenge = (next: string) => {
    setChallengeId(next);
    setPage(1);
  };
  const applyTeam = (next: string) => {
    setTeamId(next);
    setPage(1);
  };

  const openDrawer = (submission: Submission) => {
    const idx = submissions.findIndex((s) => s.id === submission.id);
    if (idx !== -1) setDrawerIndex(idx);
  };

  return (
    <div className="space-y-5">
      {/* Status pills */}
      <div className="flex flex-wrap gap-2">
        {STATUS_PILLS.map((pill) => {
          const active = status === pill.value;
          const count = summary?.[pill.countKey];
          return (
            <button
              key={pill.value}
              type="button"
              onClick={() => applyStatus(pill.value)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors',
                active
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-card text-muted-foreground hover:bg-muted',
              )}
            >
              {pill.label}
              {count != null && (
                <span
                  className={cn(
                    'rounded-full px-1.5 text-xs',
                    active ? 'bg-primary-foreground/20' : 'bg-muted text-foreground',
                  )}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Challenge + team filters */}
      <div className="flex flex-wrap gap-3">
        {!fixedChallengeId && (
          <Select value={challengeId} onValueChange={applyChallenge}>
            <SelectTrigger className="w-full sm:w-56">
              <SelectValue placeholder="All challenges" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY_OPTION}>All challenges</SelectItem>
              {(challengesData?.data ?? []).map((option) => (
                <SelectItem key={option.id} value={option.id}>
                  {option.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select value={teamId} onValueChange={applyTeam}>
          <SelectTrigger className="w-full sm:w-56">
            <SelectValue placeholder="All teams" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY_OPTION}>All teams</SelectItem>
            {(teamsData?.data ?? []).map((option) => (
              <SelectItem key={option.id} value={option.id}>
                {option.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Grid / states */}
      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="aspect-[4/5] rounded-xl" />
          ))}
        </div>
      ) : isError ? (
        <EmptyState
          icon={<ImageOff className="h-6 w-6" />}
          title="Couldn't load submissions"
          description="Something went wrong fetching submissions. Try again in a moment."
        />
      ) : submissions.length === 0 ? (
        <EmptyState
          icon={<ImageOff className="h-6 w-6" />}
          title="No submissions here"
          description={
            status === STATUS_FILTER.pending
              ? 'Nothing is waiting for review right now. New submissions will appear here as teams play.'
              : 'No submissions match these filters yet.'
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {submissions.map((submission) => (
            <SubmissionCard key={submission.id} submission={submission} onOpen={openDrawer} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.total > 0 && (
        <div className="flex items-center justify-between border-t pt-4">
          <span className="text-sm text-muted-foreground">
            {pagination.total} submission{pagination.total === 1 ? '' : 's'}
          </span>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">
              Page {pagination.page} of {Math.max(1, Math.ceil(pagination.total / pagination.limit))}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={!pagination.hasPrev}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!pagination.hasNext}
              onClick={() => setPage((p) => p + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {drawerIndex !== null && submissions[drawerIndex] && (
        <SubmissionReviewDrawer
          eventId={eventId}
          submissions={submissions}
          initialIndex={drawerIndex}
          open={drawerIndex !== null}
          onOpenChange={(open) => {
            if (!open) setDrawerIndex(null);
          }}
        />
      )}
    </div>
  );
}
